import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, filter, map, Observable } from 'rxjs';
import { SalesPartnerService } from './sales-partner.service';

export interface ReferralSession {
  referralCode: string;
  salesPartnerId: string;
  timestamp: Date;
  source: 'direct' | 'social' | 'email' | 'other';
  userAgent: string;
  ipAddress?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReferralTrackingService {
  private readonly REFERRAL_STORAGE_KEY = 'hn_referral';
  private readonly REFERRAL_EXPIRY_DAYS = 30; // Referral attribution window
  
  private currentReferralSubject = new BehaviorSubject<ReferralSession | null>(null);
  public currentReferral$ = this.currentReferralSubject.asObservable();

  constructor(
    private firestore: AngularFirestore,
    private router: Router,
    private salesPartnerService: SalesPartnerService
  ) {
    this.initializeReferralTracking();
  }

  private initializeReferralTracking(): void {
    // Listen to route changes to detect referral parameters
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.checkForReferralCode(event.urlAfterRedirects);
      }
    });

    // Load existing referral from storage
    this.loadReferralFromStorage();
  }

  private checkForReferralCode(url: string): void {
    const urlParams = new URLSearchParams(url.split('?')[1] || '');
    const referralCode = urlParams.get('ref');
    
    
    if (referralCode) {
      this.processReferralCode(referralCode);
    }
  }

  private async processReferralCode(referralCode: string): Promise<void> {
    try {
      // Validate referral code with sales partner service
      const salesPartner = await new Promise<any>((resolve, reject) => {
        const subscription = this.salesPartnerService
          .getSalesPartnerByReferralCode(referralCode)
          .subscribe({
            next: (partner) => {
              subscription.unsubscribe();
              resolve(partner);
            },
            error: (error) => {
              console.error('Sales partner service error:', error);
              subscription.unsubscribe();
              reject(error);
            }
          });
        
        // Add timeout to prevent hanging
        setTimeout(() => {
          subscription.unsubscribe();
          resolve(null);
        }, 10000);
      });

      if (salesPartner && salesPartner.isActive) {
        const referralSession: ReferralSession = {
          referralCode,
          salesPartnerId: salesPartner.id,
          timestamp: new Date(),
          source: this.detectTrafficSource(),
          userAgent: navigator.userAgent,
        };

        // Store referral in localStorage with expiry
        this.storeReferralSession(referralSession);
        
        // Update current referral subject
        this.currentReferralSubject.next(referralSession);

        // Track the referral visit
        await this.trackReferralVisit(referralSession);
      } else {
        console.warn('Invalid or inactive referral code:', referralCode);
      }
    } catch (error) {
      console.error('Error processing referral code:', error);
    }
  }

  private detectTrafficSource(): 'direct' | 'social' | 'email' | 'other' {
    const referrer = document.referrer.toLowerCase();
    
    if (!referrer) return 'direct';
    
    if (referrer.includes('facebook') || referrer.includes('twitter') || 
        referrer.includes('instagram') || referrer.includes('linkedin')) {
      return 'social';
    }
    
    if (referrer.includes('gmail') || referrer.includes('outlook') || 
        referrer.includes('mail')) {
      return 'email';
    }
    
    return 'other';
  }

  private storeReferralSession(session: ReferralSession): void {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + this.REFERRAL_EXPIRY_DAYS);
    
    const storageData = {
      session,
      expiryDate: expiryDate.toISOString()
    };
    
    localStorage.setItem(this.REFERRAL_STORAGE_KEY, JSON.stringify(storageData));
  }

  private loadReferralFromStorage(): void {
    try {
      const storedData = localStorage.getItem(this.REFERRAL_STORAGE_KEY);
      
      if (storedData) {
        const { session, expiryDate } = JSON.parse(storedData);
        
        if (new Date() < new Date(expiryDate)) {
          this.currentReferralSubject.next(session);
        } else {
          // Expired referral - remove from storage
          this.clearReferralSession();
        }
      }
    } catch (error) {
      console.error('Error loading referral from storage:', error);
      this.clearReferralSession();
    }
  }

  private async trackReferralVisit(session: ReferralSession): Promise<void> {
    try {
      const visitData = {
        salesPartnerId: session.salesPartnerId,
        referralCode: session.referralCode,
        timestamp: session.timestamp,
        source: session.source,
        userAgent: session.userAgent,
        page: window.location.pathname,
        referrer: document.referrer
      };

      await this.firestore
        .collection('referralVisits')
        .add(visitData);
    } catch (error) {
      console.error('Error tracking referral visit:', error);
    }
  }

  // Get current active referral session
  getCurrentReferral(): ReferralSession | null {
    return this.currentReferralSubject.value;
  }

  // Check if there's an active referral
  hasActiveReferral(): boolean {
    return this.currentReferralSubject.value !== null;
  }

  // Get current referral code
  getCurrentReferralCode(): string | null {
    const session = this.getCurrentReferral();
    return session ? session.referralCode : null;
  }

  // Clear referral session (e.g., after purchase)
  clearReferralSession(): void {
    localStorage.removeItem(this.REFERRAL_STORAGE_KEY);
    this.currentReferralSubject.next(null);
  }

  // Attribution method to be called during purchase
  async attributePurchaseToReferral(
    customerId: string,
    customerEmail: string,
    customerName: string,
    productId: string,
    productName: string,
    orderAmount: number,
    stripeSessionId?: string
  ): Promise<void> {
    const referralSession = this.getCurrentReferral();
    
    if (!referralSession) {
      return; // No active referral to attribute
    }

    try {
      // Get sales partner details for commission rate
      const salesPartner = await this.salesPartnerService
        .getSalesPartnerByReferralCode(referralSession.referralCode)
        .toPromise();

      if (salesPartner && salesPartner.isActive) {
        // Record the sale
        await this.salesPartnerService.recordSale(
          salesPartner.id,
          referralSession.referralCode,
          customerId,
          customerEmail,
          customerName,
          productId,
          productName,
          orderAmount,
          salesPartner.commissionRate,
          stripeSessionId
        );

        // Clear the referral session after successful attribution
        this.clearReferralSession();

        console.log('Purchase attributed to sales partner:', salesPartner.id);
      }
    } catch (error) {
      console.error('Error attributing purchase to referral:', error);
    }
  }

  // Method to manually set referral (for testing)
  setReferralForTesting(referralCode: string): void {
    this.processReferralCode(referralCode);
  }

  // Get referral statistics for admin
  getReferralStats(salesPartnerId: string): Observable<any> {
    return this.firestore
      .collection('referralVisits', ref => 
        ref.where('salesPartnerId', '==', salesPartnerId)
      )
      .valueChanges()
      .pipe(
        map(visits => {
          const now = new Date();
          const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          
          const recentVisits = visits.filter((visit: any) => 
            visit.timestamp?.toDate() >= last30Days
          );
          
          return {
            totalVisits: visits.length,
            recentVisits: recentVisits.length,
            sourceBreakdown: this.getSourceBreakdown(visits),
            conversionRate: 0 // This would be calculated by comparing visits to sales
          };
        })
      );
  }

  private getSourceBreakdown(visits: any[]): { [key: string]: number } {
    return visits.reduce((breakdown, visit) => {
      const source = visit.source || 'unknown';
      breakdown[source] = (breakdown[source] || 0) + 1;
      return breakdown;
    }, {});
  }
}